/**
 * Smart Insights Service V6
 * 3 Final Insight Types - Behavior-Based & Micro-Pattern Driven
 * 
 * Every insight outputs:
 * - type, title, subtitle, reason
 * - actions[] with askSwirlPrompt
 * - confidence score
 * - debug metadata
 */

import { supabase } from '../lib/supabase';

// ============================================
// DEBUG MODE
// ============================================
const IS_DEV = import.meta.env?.DEV || (typeof window !== 'undefined' && window.location.hostname === 'localhost');
const SMART_INSIGHTS_DEBUG = IS_DEV;
const DISABLE_CACHE_IN_DEV = true;

const debug = {
  log: (...args) => SMART_INSIGHTS_DEBUG && console.log('%c[Insights]', 'color: #8b5cf6; font-weight: bold', ...args),
  warn: (...args) => SMART_INSIGHTS_DEBUG && console.warn('%c[Insights]', 'color: #f59e0b; font-weight: bold', ...args),
  error: (...args) => console.error('%c[Insights Error]', 'color: #ef4444; font-weight: bold', ...args),
  group: (label) => SMART_INSIGHTS_DEBUG && console.group(`%c[Insights] ${label}`, 'color: #8b5cf6; font-weight: bold'),
  groupEnd: () => SMART_INSIGHTS_DEBUG && console.groupEnd(),
  rule: (ruleName, triggered, inputs, reason, confidence) => {
    if (!SMART_INSIGHTS_DEBUG) return;
    const icon = triggered ? '✅' : '❌';
    const color = triggered ? '#22c55e' : '#6b7280';
    console.log(`%c${icon} ${ruleName}`, `color: ${color}; font-weight: bold`);
    if (triggered) {
      console.log(`   Confidence: ${confidence} | Reason: ${reason}`);
    }
    console.log('   Inputs:', inputs);
  }
};

// Storage keys
const SUGGESTIONS_CACHE_KEY = 'swirl_suggestions_cache_v6';
const SUGGESTIONS_TIMESTAMP_KEY = 'swirl_suggestions_timestamp_v6';
const PINNED_SUGGESTIONS_KEY = 'swirl_pinned_suggestions';
const DISMISSED_SUGGESTIONS_KEY = 'swirl_dismissed_suggestions';
const SNOOZED_SUGGESTIONS_KEY = 'swirl_snoozed_suggestions';
const LAST_ITEM_COUNT_KEY = 'swirl_last_item_count';

const CACHE_DURATION_MS = 4 * 60 * 60 * 1000;

// ============================================
// 3 FINAL INSIGHT TYPES + NEGLECTED FOCUS
// ============================================
const INSIGHT_TYPES = {
  DEEP_DIVE: {
    id: 'deep_dive',
    title: 'Deep Dive Opportunity',
    icon: 'Layers',
    color: 'indigo',
    actions: [
      {
        label: 'Create a Focus',
        type: 'create_focus',
        askSwirlPrompt: null,
      },
      {
        label: 'Ask Swirl to go deeper',
        type: 'ask_swirl',
        askSwirlPrompt: 'Analyze this collection and help me extract the key patterns and what to do with them.',
      },
    ],
  },
  EVENING_PATTERN: {
    id: 'evening_pattern',
    title: 'Evening Pattern',
    icon: 'Moon',
    color: 'violet',
    actions: [
      {
        label: 'Create a Focus',
        type: 'create_focus',
        askSwirlPrompt: null,
      },
      {
        label: 'Ask Swirl to go deeper',
        type: 'ask_swirl',
        askSwirlPrompt: 'Help me design an evening routine that leverages when I am most active.',
      },
    ],
  },
  EMERGING_DIRECTION: {
    id: 'emerging_direction',
    title: 'Emerging Direction',
    icon: 'Sparkles',
    color: 'teal',
    actions: [
      {
        label: 'Create a Focus',
        type: 'create_focus',
        askSwirlPrompt: null,
      },
      {
        label: 'Ask Swirl to go deeper',
        type: 'ask_swirl',
        askSwirlPrompt: 'Help me understand where this new direction might lead and what I should explore next.',
      },
    ],
  },
  NEGLECTED_FOCUS: {
    id: 'neglected_focus',
    title: 'Neglected Focus',
    icon: 'Clock',
    color: 'amber',
    actions: [
      {
        label: 'Open Focus',
        type: 'open_focus',
        askSwirlPrompt: null,
      },
      {
        label: 'Build a plan',
        type: 'ask_swirl',
        askSwirlPrompt: 'Help me build a plan to revive this focus and make progress on it.',
      },
    ],
  },
  TIME_GAPS: {
    id: 'time_gaps',
    title: 'Time Gap Detected',
    icon: 'TrendingDown',
    color: 'rose',
    actions: [
      {
        label: 'Explore this theme',
        type: 'open_theme',
        askSwirlPrompt: null,
      },
      {
        label: 'Ask Swirl why',
        type: 'ask_swirl',
        askSwirlPrompt: 'Why did I stop engaging with this topic? Help me decide if I should revisit it.',
      },
    ],
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function isCacheValid(itemCount = null) {
  if (IS_DEV && DISABLE_CACHE_IN_DEV) return false;
  try {
    const timestamp = localStorage.getItem(SUGGESTIONS_TIMESTAMP_KEY);
    if (!timestamp) return false;
    const age = Date.now() - parseInt(timestamp, 10);
    if (age >= CACHE_DURATION_MS) return false;
    if (itemCount !== null) {
      const lastCount = parseInt(localStorage.getItem(LAST_ITEM_COUNT_KEY) || '0', 10);
      if (Math.abs(itemCount - lastCount) >= 3) return false;
    }
    return true;
  } catch {
    return false;
  }
}

function getCachedSuggestions() {
  if (IS_DEV && DISABLE_CACHE_IN_DEV) return null;
  try {
    const cached = localStorage.getItem(SUGGESTIONS_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function cacheSuggestions(data, userId, itemCount = null) {
  try {
    localStorage.setItem(SUGGESTIONS_CACHE_KEY, JSON.stringify({ userId, ...data }));
    localStorage.setItem(SUGGESTIONS_TIMESTAMP_KEY, Date.now().toString());
    if (itemCount !== null) {
      localStorage.setItem(LAST_ITEM_COUNT_KEY, itemCount.toString());
    }
  } catch (e) {
    debug.warn('Cache write failed:', e);
  }
}

function getDismissedIds() {
  try {
    const dismissed = localStorage.getItem(DISMISSED_SUGGESTIONS_KEY);
    return dismissed ? JSON.parse(dismissed) : [];
  } catch {
    return [];
  }
}

function isSnoozed(suggestionId) {
  try {
    const snoozed = JSON.parse(localStorage.getItem(SNOOZED_SUGGESTIONS_KEY) || '{}');
    const snoozeUntil = snoozed[suggestionId];
    return snoozeUntil && Date.now() < snoozeUntil;
  } catch {
    return false;
  }
}

function validateItems(items) {
  if (!Array.isArray(items)) return [];
  return items.filter(item => item.created_at && (item.title || item.description || item.url || item.auto_tags?.topics?.length > 0));
}

// Topics to EXCLUDE - generic, meaningless, or too vague
const EXCLUDED_TOPICS = new Set([
  'man', 'woman', 'person', 'people', 'human', 'male', 'female', 'adult', 'child',
  'photo', 'image', 'picture', 'photograph', 'screenshot', 'video', 'clip',
  'room', 'building', 'street', 'outdoor', 'indoor', 'background', 'wall',
  'object', 'item', 'thing', 'stuff', 'standing', 'sitting', 'looking',
  'modern', 'beautiful', 'nice', 'good', 'white', 'black', 'blue', 'red',
  'content', 'post', 'text', 'day', 'night', 'here', 'there', 'style',
]);

function isActionableTopic(topic) {
  if (!topic || topic.length < 3) return false;
  if (EXCLUDED_TOPICS.has(topic.toLowerCase())) return false;
  if (/^\d+$/.test(topic)) return false;
  return true;
}

function getItemTopics(item) {
  const rawTopics = [
    ...(item.auto_tags?.topics || []),
    ...(item.meta?.ai_analysis?.topics || []),
    ...(item.primary_tags || [])
  ].filter(t => t && typeof t === 'string').map(t => t.toLowerCase().trim());
  
  return rawTopics.filter(isActionableTopic);
}

function extractMicroTopics(items) {
  const topicCounts = {};
  items.forEach(item => {
    getItemTopics(item).forEach(topic => {
      topicCounts[topic] = (topicCounts[topic] || 0) + 1;
    });
  });
  
  return Object.entries(topicCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([topic, count]) => ({ topic, count }));
}

// ============================================
// INSIGHT DETECTION RULES (3 FINAL)
// ============================================

/**
 * 1. DEEP DIVE OPPORTUNITY
 * Trigger: 8+ items in a cluster (lowered from 12 for better detection)
 */
function detectDeepDive(items) {
  const now = new Date();
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const recentItems = items.filter(item => new Date(item.created_at) >= monthAgo);
  
  if (recentItems.length < 8) {
    debug.rule('Deep Dive', false, { items: recentItems.length }, 'Need 8+ items', 0);
    return null;
  }
  
  const microTopics = extractMicroTopics(recentItems);
  
  if (microTopics.length === 0 || microTopics[0].count < 8) {
    debug.rule('Deep Dive', false, { topTopic: microTopics[0]?.topic, count: microTopics[0]?.count }, 'No cluster with 8+ items', 0);
    return null;
  }
  
  // Get top micro-topics (not just the biggest one)
  const topMicros = microTopics.filter(m => m.count >= 3).slice(0, 5);
  const mainTopic = microTopics[0];
  
  // Build micro-pattern description
  const microPatterns = topMicros.map(m => m.topic).join(', ');
  const totalCount = recentItems.length;
  
  // Interpret behavioral meaning
  let behavioralMeaning = '';
  if (mainTopic.count >= 12) {
    behavioralMeaning = 'This is not casual scrolling. You are shaping a clear system or preparing for a future output.';
  } else {
    behavioralMeaning = 'You are going deep here. This collection shares a mood and direction.';
  }
  
  const confidence = Math.min(95, 65 + (mainTopic.count - 8) * 3);
  
  debug.rule('Deep Dive', true, { mainTopic: mainTopic.topic, count: mainTopic.count, totalItems: totalCount }, behavioralMeaning, confidence);
  
  const relatedItems = recentItems.filter(item => 
    getItemTopics(item).includes(mainTopic.topic)
  ).slice(0, 5);
  
  return {
    id: `deep_dive_${Date.now()}`,
    type: INSIGHT_TYPES.DEEP_DIVE,
    title: 'Deep Dive Opportunity',
    subtitle: `You collected ${microPatterns}. ${behavioralMeaning.split('.')[0]}.`,
    reason: `${totalCount} items focusing on ${microPatterns}. ${behavioralMeaning} These items cluster around a specific direction worth organizing.`,
    actions: [
      ...INSIGHT_TYPES.DEEP_DIVE.actions,
      {
        label: 'Group items into sub-themes',
        type: 'organize',
        askSwirlPrompt: 'Help me break this collection into sub-themes and identify the key patterns.',
      },
      {
        label: 'Extract top 5 most valuable patterns',
        type: 'synthesize',
        askSwirlPrompt: 'Show me the 5 most important patterns in this collection.',
      },
      {
        label: 'Turn the collection into a plan',
        type: 'actionize',
        askSwirlPrompt: 'Help me turn this research into an actionable plan or output.',
      },
    ],
    confidence,
    relatedItems,
    data: { mainTopic: mainTopic.topic, count: totalCount, microTopics: topMicros.map(m => m.topic) },
    metadata: {
      rule: 'deep_dive',
      trigger: '12-20+ items in cluster',
      inputs: { totalItems: totalCount, mainTopic: mainTopic.topic, mainCount: mainTopic.count },
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * 2. EVENING PATTERN
 * Trigger: 50%+ of saves in evening/night hours (lowered from 60% for better detection)
 */
function detectEveningPattern(items) {
  const now = new Date();
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
  const recentItems = items.filter(item => new Date(item.created_at) >= twoWeeksAgo);
  
  if (recentItems.length < 7) {
    debug.rule('Evening Pattern', false, { items: recentItems.length }, 'Need 7+ items', 0);
    return null;
  }
  
  // Categorize by time block
  let eveningSaves = 0; // 6pm-10pm
  let nightSaves = 0; // 10pm-5am
  let morningSaves = 0; // 5am-12pm
  let afternoonSaves = 0; // 12pm-6pm
  
  recentItems.forEach(item => {
    const hour = new Date(item.created_at).getHours();
    if (hour >= 18 && hour < 22) eveningSaves++;
    else if (hour >= 22 || hour < 5) nightSaves++;
    else if (hour >= 5 && hour < 12) morningSaves++;
    else afternoonSaves++;
  });
  
  const total = recentItems.length;
  const eveningPct = Math.round((eveningSaves / total) * 100);
  const nightPct = Math.round((nightSaves / total) * 100);
  const combinedEveningNight = eveningSaves + nightSaves;
  const combinedPct = Math.round((combinedEveningNight / total) * 100);
  
  // Trigger: 50%+ in evening OR 50%+ combined evening+night (lowered from 60%)
  if (eveningPct < 50 && nightPct < 50 && combinedPct < 50) {
    debug.rule('Evening Pattern', false, { eveningPct, nightPct, combinedPct }, 'Need 50%+ in evening/night', 0);
  return null;
}

  // Determine behavioral meaning
  let behavioralMeaning = '';
  let timeWindow = '';
  let dominantPct = 0;
  
  if (nightPct >= 50) {
    timeWindow = 'late at night';
    dominantPct = nightPct;
    behavioralMeaning = 'You use late nights to process ideas and make decisions. This is your creative and planning window.';
  } else if (eveningPct >= 50) {
    timeWindow = 'in the evening';
    dominantPct = eveningPct;
    behavioralMeaning = 'You use evenings to refine how you want to look and operate. This is your decision-making zone.';
  } else {
    timeWindow = 'in evening and night hours';
    dominantPct = combinedPct;
    behavioralMeaning = 'Your evenings are when you plan, refine, and prepare. This is your creative energy window.';
  }
  
  const confidence = Math.min(95, 50 + dominantPct / 2);
  
  debug.rule('Evening Pattern', true, { timeWindow, pct: dominantPct }, behavioralMeaning, confidence);
  
  return {
    id: `evening_pattern_${Date.now()}`,
    type: INSIGHT_TYPES.EVENING_PATTERN,
    title: 'Evening Pattern',
    subtitle: `Most of your saves happen ${timeWindow}. ${behavioralMeaning.split('.')[0]}.`,
    reason: `${dominantPct}% of your recent saves are ${timeWindow}. ${behavioralMeaning} This pattern reveals when your brain is most engaged.`,
    actions: [
      ...INSIGHT_TYPES.EVENING_PATTERN.actions,
      {
        label: 'Shape an evening flow',
        type: 'routine',
        askSwirlPrompt: 'Help me design an evening routine that uses this peak window effectively.',
      },
      {
        label: 'Use this window for planning',
        type: 'plan',
        askSwirlPrompt: 'Show me how to structure my evening hours for maximum creative output.',
      },
      {
        label: 'Reduce distractions during peak hours',
        type: 'optimize',
        askSwirlPrompt: 'Help me protect my evening window from distractions.',
      },
    ],
    confidence,
    relatedItems: [],
    data: { timeWindow, percentage: dominantPct, eveningSaves, nightSaves, total },
    metadata: {
      rule: 'evening_pattern',
      trigger: '60-80%+ evening/night saves',
      inputs: { eveningPct, nightPct, combinedPct, total },
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * 3. EMERGING DIRECTION
 * Trigger: 3+ related items in past 3 weeks (expanded window for better detection)
 * Returns placeholder if no pattern found
 */
function detectEmergingDirection(items) {
  const now = new Date();
  const threeWeeksAgo = new Date(now.getTime() - 21 * 24 * 60 * 60 * 1000);
  const recentItems = items.filter(item => new Date(item.created_at) >= threeWeeksAgo);
  
  // If not enough items at all, return placeholder
  if (recentItems.length < 3) {
    debug.rule('Emerging Direction', false, { items: recentItems.length }, 'Not enough items - showing placeholder', 0);
    return {
      id: `emerging_direction_placeholder_${Date.now()}`,
      type: INSIGHT_TYPES.EMERGING_DIRECTION,
      title: 'Emerging Direction',
      subtitle: 'Save more items to discover new patterns forming',
      reason: 'Emerging Direction insights appear when you save 3+ items about a new topic within a few weeks. Keep saving content and this card will detect early signals of new interests as they form.',
      actions: [
        {
          label: 'Learn more about insights',
          type: 'info',
          askSwirlPrompt: null,
        },
        {
          label: 'Ask Swirl for tips',
          type: 'ask_swirl',
          askSwirlPrompt: 'What kinds of patterns should I look for in my saves?',
        },
      ],
      confidence: 0,
      relatedItems: [],
      data: { isPlaceholder: true, reason: 'insufficient_data', itemCount: recentItems.length },
      metadata: {
        rule: 'emerging_direction_placeholder',
        trigger: 'Not enough items',
        inputs: { totalRecent: recentItems.length },
        timestamp: new Date().toISOString(),
      },
    };
  }
  
  const microTopics = extractMicroTopics(recentItems);
  
  // If no clear cluster, return placeholder
  if (microTopics.length === 0 || microTopics[0].count < 3) {
    debug.rule('Emerging Direction', false, { topTopic: microTopics[0]?.topic, count: microTopics[0]?.count }, 'No cluster found - showing placeholder', 0);
    return {
      id: `emerging_direction_placeholder_${Date.now()}`,
      type: INSIGHT_TYPES.EMERGING_DIRECTION,
      title: 'Emerging Direction',
      subtitle: 'Your saves are scattered—no clear pattern yet',
      reason: 'Emerging Direction insights appear when related items cluster together. Your recent saves are diverse, which means no single direction is forming yet. Save more items in areas that interest you to help patterns emerge.',
      actions: [
        {
          label: 'View all recent saves',
          type: 'view_items',
          askSwirlPrompt: null,
        },
        {
          label: 'Ask Swirl for suggestions',
          type: 'ask_swirl',
          askSwirlPrompt: 'What should I focus on saving to build meaningful patterns?',
        },
      ],
      confidence: 0,
      relatedItems: [],
      data: { isPlaceholder: true, reason: 'no_cluster', itemCount: recentItems.length, topicsFound: microTopics.length },
      metadata: {
        rule: 'emerging_direction_placeholder',
        trigger: 'No clear cluster',
        inputs: { totalRecent: recentItems.length, topTopic: microTopics[0]?.topic, topCount: microTopics[0]?.count },
        timestamp: new Date().toISOString(),
      },
    };
  }
  
  // Get micro-cluster
  const topMicros = microTopics.filter(m => m.count >= 2).slice(0, 4);
  const microPatterns = topMicros.map(m => m.topic).join(', ');
  const mainTopic = microTopics[0];
  
  // Interpret what this direction means
  let directionMeaning = '';
  const patterns = topMicros.map(m => m.topic);
  
  // Behavioral interpretation based on patterns
  if (patterns.some(p => p.includes('travel') || p.includes('japan') || p.includes('trip'))) {
    directionMeaning = 'This looks like the early signals of planning a trip.';
  } else if (patterns.some(p => p.includes('negotiation') || p.includes('sales') || p.includes('objection'))) {
    directionMeaning = 'This is a clear sharpening phase. You are leveling up professional skillsets.';
  } else if (patterns.some(p => p.includes('mobility') || p.includes('workout') || p.includes('training'))) {
    directionMeaning = 'You are building a new fitness or movement system.';
  } else if (patterns.some(p => p.includes('style') || p.includes('fashion') || p.includes('clothing'))) {
    directionMeaning = 'You are refining your personal style system.';
  } else {
    directionMeaning = 'A new interest is forming. Early signals like this often become bigger projects.';
  }
  
  const confidence = Math.min(85, 60 + mainTopic.count * 3);
  
  debug.rule('Emerging Direction', true, { patterns: microPatterns, count: mainTopic.count }, directionMeaning, confidence);
  
  const relatedItems = recentItems.filter(item => 
    getItemTopics(item).some(t => patterns.includes(t))
  ).slice(0, 5);
  
  return {
    id: `emerging_direction_${Date.now()}`,
    type: INSIGHT_TYPES.EMERGING_DIRECTION,
    title: 'Emerging Direction',
    subtitle: `You saved ${microPatterns}. ${directionMeaning.split('.')[0]}.`,
    reason: `${mainTopic.count} items about ${microPatterns} in the last 3 weeks. ${directionMeaning} This cluster is worth tracking.`,
    actions: [
      ...INSIGHT_TYPES.EMERGING_DIRECTION.actions,
      {
        label: 'Group items into a plan',
        type: 'organize',
        askSwirlPrompt: 'Help me organize these items into a structured plan or direction.',
      },
      {
        label: 'Build a prep list',
        type: 'prepare',
        askSwirlPrompt: 'What should I explore next based on this emerging direction?',
      },
    ],
    confidence,
    relatedItems,
    data: { mainTopic: mainTopic.topic, count: mainTopic.count, microTopics: patterns, weeks: 3 },
    metadata: {
      rule: 'emerging_direction',
      trigger: '3+ related items in 3 weeks',
      inputs: { totalRecent: recentItems.length, mainTopic: mainTopic.topic, mainCount: mainTopic.count },
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * 4. TIME GAPS - Theme/topic abandonment
 * Trigger: User stopped engaging with a theme they used to save frequently
 * Returns largest gap (most dramatic abandonment)
 * 
 * Rules:
 * - At least 3 items saved within a 14-day window (shows focused interest, not scattered saves)
 * - Last save was 21+ days ago (significant gap)
 * - Uses standardized getItemTopics() for consistent topic extraction
 */
async function detectTimeGaps(items, userId) {
  try {
    // Group items by topics using standardized helper
    const topicTimeline = {};
    
    items.forEach(item => {
      const topics = getItemTopics(item); // Use standardized helper
      const createdAt = new Date(item.created_at);
      
      topics.forEach(topic => {
        if (!topicTimeline[topic]) {
          topicTimeline[topic] = [];
        }
        topicTimeline[topic].push(createdAt);
      });
    });
    
    // Find topics with gaps > 21 days (3 weeks)
    const now = new Date();
    const gaps = [];
    
    Object.entries(topicTimeline).forEach(([topic, dates]) => {
      // Need at least 3 saves to detect a theme
      if (dates.length < 3) return;
      
      // Sort dates (oldest first)
      dates.sort((a, b) => a - b);
      
      const mostRecent = dates[dates.length - 1];
      const daysSinceLastSave = Math.floor((now - mostRecent) / (1000 * 60 * 60 * 24));
      
      // CRITICAL: Check if there was a 14-day cluster (at least 3 items within 14 days)
      // This prevents scattered interest from being flagged as a "theme"
      let hasCluster = false;
      for (let i = 0; i <= dates.length - 3; i++) {
        const windowStart = dates[i];
        const windowEnd = dates[i + 2]; // Check if 3 items fit in 14 days
        const windowDays = (windowEnd - windowStart) / (1000 * 60 * 60 * 24);
        
        if (windowDays <= 14) {
          hasCluster = true;
          break;
        }
      }
      
      // Only flag if:
      // - At least 3 items were saved within a 14-day window (shows focused interest)
      // - Last save was 21+ days ago
      if (hasCluster && daysSinceLastSave >= 21) {
        gaps.push({
          topic,
          daysSinceLastSave,
          totalSaves: dates.length,
          firstSave: dates[0],
          lastSave: mostRecent,
        });
      }
    });
    
    if (gaps.length === 0) {
      debug.rule('Time Gaps', false, { topicCount: Object.keys(topicTimeline).length }, 'No significant gaps detected (no 14-day clusters)', 0);
      return null;
    }
    
    // Sort by days since last save (biggest gap first)
    gaps.sort((a, b) => b.daysSinceLastSave - a.daysSinceLastSave);
    
    const biggestGap = gaps[0];
    const confidence = Math.min(90, 60 + Math.floor(biggestGap.daysSinceLastSave / 7) * 3);
    
    // Find related items for this topic (use standardized helper)
    const relatedItems = items
      .filter(item => {
        const topics = getItemTopics(item); // Use standardized helper
        return topics.includes(biggestGap.topic);
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5)
      .map(item => ({
        id: item.id,
        title: item.title || 'Untitled',
        type: item.source_type || 'content',
        created_at: item.created_at,
        thumbnail: item.thumbnail_url || item.image_url,
      }));
    
    debug.rule('Time Gaps', true, { 
      topic: biggestGap.topic, 
      daysSinceLastSave: biggestGap.daysSinceLastSave,
      totalSaves: biggestGap.totalSaves
    }, `"${biggestGap.topic}" hasn't been touched in ${biggestGap.daysSinceLastSave} days`, confidence);
    
    return {
      id: `time_gap_${biggestGap.topic.toLowerCase().replace(/\s+/g, '_')}`,
      type: INSIGHT_TYPES.TIME_GAPS,
      title: 'Time Gap Detected',
      subtitle: `You haven't engaged with "${biggestGap.topic}" in ${biggestGap.daysSinceLastSave} days`,
      reason: `You saved ${biggestGap.totalSaves} items about "${biggestGap.topic}" between ${biggestGap.firstSave.toLocaleDateString()} and ${biggestGap.lastSave.toLocaleDateString()}, but then stopped. When a theme goes quiet for ${biggestGap.daysSinceLastSave}+ days after consistent attention, it's worth asking: did priorities shift, or is this something you want to revive?`,
      actions: [
        {
          label: 'View these items',
          type: 'filter_theme',
          askSwirlPrompt: null,
          theme: biggestGap.topic,
        },
        {
          label: 'Ask Swirl why',
          type: 'ask_swirl',
          askSwirlPrompt: `I used to save a lot about "${biggestGap.topic}" but stopped ${biggestGap.daysSinceLastSave} days ago. Help me understand why and whether I should revisit it.`,
        },
      ],
      confidence,
      relatedItems,
      data: { 
        topic: biggestGap.topic,
        daysSinceLastSave: biggestGap.daysSinceLastSave,
        totalSaves: biggestGap.totalSaves,
        firstSave: biggestGap.firstSave,
        lastSave: biggestGap.lastSave,
      },
      metadata: {
        rule: 'time_gaps',
        trigger: '21+ days since last save for a theme with 3+ saves within a 14-day cluster',
        inputs: { topic: biggestGap.topic, gap: biggestGap.daysSinceLastSave },
        timestamp: new Date().toISOString(),
      },
    };
  } catch (error) {
    debug.error('Error detecting time gaps:', error);
    return null;
  }
}

/**
 * 5. NEGLECTED FOCUS
 * Trigger: Focus has last_active_at older than 14 days
 * Returns most neglected focus (largest days since active)
 */
async function detectNeglectedFocus(userId) {
  try {
    // Get all focuses for user
    const { data: focuses, error } = await supabase
      .schema('public')
      .from('focuses')
      .select('*')
      .eq('user_id', userId);
    
    if (error) {
      debug.warn('Failed to fetch focuses for neglect detection:', error);
      return null;
    }
    
    if (!focuses || focuses.length === 0) {
      debug.rule('Neglected Focus', false, { focusCount: 0 }, 'No focuses exist', 0);
      return null;
    }
    
    const now = new Date();
    const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    // Find focuses that are neglected (last_active_at > 14 days ago AND not null)
    const neglectedFocuses = focuses.filter(focus => {
      if (!focus.last_active_at) return false; // Skip focuses that were never active
      const lastActive = new Date(focus.last_active_at);
      return lastActive < fourteenDaysAgo;
    });
    
    if (neglectedFocuses.length === 0) {
      debug.rule('Neglected Focus', false, { neglectedCount: 0, totalFocuses: focuses.length }, 'No neglected focuses', 0);
      return null;
    }
    
    // Sort by oldest last_active_at (most neglected first)
    neglectedFocuses.sort((a, b) => 
      new Date(a.last_active_at).getTime() - new Date(b.last_active_at).getTime()
    );
    
    const mostNeglected = neglectedFocuses[0];
    const lastActiveDate = new Date(mostNeglected.last_active_at);
    const daysSinceActive = Math.floor((now - lastActiveDate) / (1000 * 60 * 60 * 24));
    
    // Fetch related conversations (last 3)
    const { data: conversations } = await supabase
      .schema('public')
      .from('conversations')
      .select('id, title, updated_at')
      .eq('focus_id', mostNeglected.id)
      .order('updated_at', { ascending: false })
      .limit(3);
    
    const relatedConversations = (conversations || []).map(c => ({
      id: c.id,
      title: c.title || 'Untitled conversation',
      type: 'conversation',
      created_at: c.updated_at
    }));
    
    const confidence = Math.min(95, 70 + Math.floor(daysSinceActive / 7) * 5);
    
    debug.rule('Neglected Focus', true, { 
      focusTitle: mostNeglected.title, 
      daysSinceActive 
    }, `Focus hasn't been touched in ${daysSinceActive} days`, confidence);
    
    return {
      id: `neglected_focus_${mostNeglected.id}`,
      type: INSIGHT_TYPES.NEGLECTED_FOCUS,
      title: 'Neglected Focus',
      subtitle: `${mostNeglected.title} hasn't been active in ${daysSinceActive} days`,
      reason: `Your Focus "${mostNeglected.title}" was last active ${daysSinceActive} days ago. When focuses go quiet for this long, it usually means either the project stalled or priorities shifted. This is worth revisiting to either archive it or revive it with fresh momentum.`,
      actions: [
        {
          label: 'Open Focus',
          type: 'open_focus',
          askSwirlPrompt: null,
          focusId: mostNeglected.id,
        },
        {
          label: 'Build a plan',
          type: 'ask_swirl',
          askSwirlPrompt: `Help me revive my "${mostNeglected.title}" focus and make progress on it.`,
        },
        {
          label: 'Archive this focus',
          type: 'archive_focus',
          askSwirlPrompt: null,
          focusId: mostNeglected.id,
        },
      ],
      confidence,
      relatedItems: relatedConversations,
      data: { 
        focusId: mostNeglected.id,
        focusTitle: mostNeglected.title,
        daysSinceActive,
        lastActiveAt: mostNeglected.last_active_at 
      },
      metadata: {
        rule: 'neglected_focus',
        trigger: '14+ days since last_active_at',
        inputs: { focusId: mostNeglected.id, daysSinceActive },
        timestamp: new Date().toISOString(),
      },
    };
  } catch (error) {
    debug.error('Error detecting neglected focus:', error);
    return null;
  }
}

// ============================================
// MAIN DETECTION ENGINE
// ============================================

async function detectAllInsights(items, userId) {
  debug.group('🔍 Smart Insights V8 - 5 Rules (3 from items + 2 from focuses/themes)');
  if (SMART_INSIGHTS_DEBUG) {
    console.log('%c══════════════════════════════════════════════════', 'color: #8b5cf6');
  }
  debug.log(`Analyzing ${items.length} items + focuses + theme history...`);
  
  const dismissedIds = getDismissedIds();
  const allInsights = [];
  
  // Neglected Focus is SPECIAL - it can't be dismissed and always shows if it exists
  const neglectedFocus = await detectNeglectedFocus(userId);
  const pinnedInsights = []; // Always shown first, can't be dismissed
  
  if (neglectedFocus) {
    pinnedInsights.push(neglectedFocus);
    debug.log('📌 Auto-pinned: Neglected Focus (always shows, can\'t be dismissed)');
    
    // VERIFY: Neglected Focus bypasses dismissal even if user tried to dismiss it
    if (SMART_INSIGHTS_DEBUG && dismissedIds.includes('neglected_focus')) {
      console.warn('⚠️ User dismissed "neglected_focus" but it still shows (intended behavior)');
    }
  }
  
  const detectors = [
    { name: 'Deep Dive', fn: () => detectDeepDive(items) },
    { name: 'Evening Pattern', fn: () => detectEveningPattern(items) },
    { name: 'Emerging Direction', fn: () => detectEmergingDirection(items) },
    { name: 'Time Gaps', fn: () => detectTimeGaps(items, userId) }, // New detector
  ];
  
  if (SMART_INSIGHTS_DEBUG) {
    console.log('%c\n📋 EVALUATING 4 RULES:', 'color: #f59e0b; font-weight: bold');
  }
  
  // Run all detectors (some are async)
  for (const { name, fn } of detectors) {
    const result = await fn();
    if (result) {
      // VERIFY: Regular insights respect dismissal
      if (!dismissedIds.includes(result.type.id) && !isSnoozed(result.type.id)) {
        allInsights.push(result);
      } else if (SMART_INSIGHTS_DEBUG) {
        console.log(`🚫 Skipped "${result.type.id}" (dismissed or snoozed)`);
      }
    }
  }
  
  // Sort by confidence (highest first) - placeholders (confidence 0) will be last
  allInsights.sort((a, b) => b.confidence - a.confidence);
  
  // Take top 3 insights (prioritize real insights over placeholders)
  const topInsights = allInsights.slice(0, 3);
  
  // Merge: Pinned insights first, then top 3 regular insights
  const finalInsights = [...pinnedInsights, ...topInsights];
  
  if (SMART_INSIGHTS_DEBUG) {
    console.log('%c\n✅ DETECTED INSIGHTS:', 'color: #22c55e; font-weight: bold');
    finalInsights.forEach((s, i) => {
      const isPlaceholder = s.data?.isPlaceholder ? ' [PLACEHOLDER]' : '';
      const isPinned = pinnedInsights.includes(s) ? ' 📌' : '';
      console.log(`   ${i + 1}. ${s.title} (confidence: ${s.confidence})${isPlaceholder}${isPinned}`);
      console.log(`      "${s.subtitle}"`);
    });
    console.log('%c══════════════════════════════════════════════════', 'color: #8b5cf6');
  }
  debug.groupEnd();
  
  return finalInsights;
}

// ============================================
// PUBLIC API
// ============================================

export async function getSuggestionsForUser(userId, items = [], momentumMetrics = null, forceRefresh = false) {
  debug.group('Smart Insights Evaluation');
  debug.log(`Mode: ${IS_DEV ? 'DEV (no cache)' : 'PROD'} | Force: ${forceRefresh}`);
  
  const validItems = validateItems(items);
  debug.log(`Items: ${items.length} → Valid: ${validItems.length}`);
  
  const shouldUseCache = !IS_DEV && !DISABLE_CACHE_IN_DEV && !forceRefresh;
  
  if (shouldUseCache) {
    const cached = getCachedSuggestions();
    if (cached?.userId === userId && cached?.suggestions && isCacheValid(validItems.length)) {
      const dismissedIds = getDismissedIds();
      const validCached = cached.suggestions.filter(s => !dismissedIds.includes(s.type?.id) && !isSnoozed(s.type?.id));
      if (validCached.length > 0) {
        debug.log('Using cached insights');
        debug.groupEnd();
        return validCached;
      }
    }
  }
  
  const insights = await detectAllInsights(validItems, userId);
  cacheSuggestions({ suggestions: insights }, userId, validItems.length);
  
  debug.groupEnd();
  return insights;
}

export async function getPinnedSuggestions(userId) {
  try {
    const localPinned = localStorage.getItem(PINNED_SUGGESTIONS_KEY);
    const localData = localPinned ? JSON.parse(localPinned) : [];
    
    // Wait for auth session first
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user?.id) return localData;
    
    // Use authenticated user ID
    const authenticatedUserId = session.session.user.id;
    if (!authenticatedUserId) return localData;
    
    try {
      const { data, error } = await supabase
        .from('pinned_insights')
        .select('*')
        .eq('user_id', authenticatedUserId)
        .order('pinned_at', { ascending: false });
      
      if (!error && data?.length > 0) {
        return data.map(row => ({
          id: row.suggestion_id,
          type: row.suggestion_type,
          shortTitle: row.short_title,
          subtitle: row.subtitle,
          whyShowing: row.why_showing,
          relatedItems: row.related_items || [],
          metrics: row.metrics || {},
          pinnedAt: new Date(row.pinned_at).getTime()
        }));
      }
    } catch (e) {
      debug.warn('DB fetch failed:', e.message);
    }
    
    return localData;
  } catch (e) {
    debug.error('getPinnedSuggestions failed:', e);
    return [];
  }
}

export async function pinSuggestion(suggestion, userId) {
  try {
    // Wait for auth session first
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user?.id) {
      // Fallback to local storage only
      const pinned = JSON.parse(localStorage.getItem(PINNED_SUGGESTIONS_KEY) || '[]');
      if (!pinned.find(p => p.id === suggestion.id)) {
        pinned.push({ ...suggestion, pinnedAt: Date.now() });
        localStorage.setItem(PINNED_SUGGESTIONS_KEY, JSON.stringify(pinned));
      }
      return await getPinnedSuggestions();
    }
    
    // Use authenticated user ID
    const authenticatedUserId = session.session.user.id;
    
    const typeId = typeof suggestion.type === 'string' ? suggestion.type : (suggestion.type?.id || 'none');
    
    const pinned = JSON.parse(localStorage.getItem(PINNED_SUGGESTIONS_KEY) || '[]');
    if (!pinned.find(p => p.id === suggestion.id)) {
      pinned.push({ ...suggestion, pinnedAt: Date.now() });
      localStorage.setItem(PINNED_SUGGESTIONS_KEY, JSON.stringify(pinned));
    }
    
    try {
      await supabase.from('pinned_insights').upsert({
        user_id: authenticatedUserId,
        suggestion_id: suggestion.id,
        suggestion_type: typeId,
        short_title: suggestion.title || suggestion.shortTitle,
        subtitle: suggestion.subtitle,
        why_showing: suggestion.reason || suggestion.whyShowing,
        related_items: suggestion.relatedItems || [],
        metrics: suggestion.data || {},
        pinned_at: new Date().toISOString()
      }, { onConflict: 'user_id,suggestion_id' });
    } catch (e) {
      debug.warn('DB pin sync failed:', e.message);
    }
    
    return await getPinnedSuggestions();
  } catch (e) {
    debug.error('Pin failed:', e);
    return await getPinnedSuggestions();
  }
}

export async function unpinSuggestion(suggestionId, userId) {
  try {
    // Wait for auth session first
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user?.id) {
      // Fallback to local storage only
      const pinned = JSON.parse(localStorage.getItem(PINNED_SUGGESTIONS_KEY) || '[]');
      const newPinned = pinned.filter(p => p.id !== suggestionId);
      localStorage.setItem(PINNED_SUGGESTIONS_KEY, JSON.stringify(newPinned));
      return await getPinnedSuggestions();
    }
    
    // Use authenticated user ID
    const authenticatedUserId = session.session.user.id;
    
    const pinned = JSON.parse(localStorage.getItem(PINNED_SUGGESTIONS_KEY) || '[]');
    const newPinned = pinned.filter(p => p.id !== suggestionId);
    localStorage.setItem(PINNED_SUGGESTIONS_KEY, JSON.stringify(newPinned));
    
    try {
      await supabase.from('pinned_insights')
        .delete()
        .eq('user_id', authenticatedUserId)
        .eq('suggestion_id', suggestionId);
    } catch (e) {
      debug.warn('DB unpin sync failed:', e.message);
    }
    
    return await getPinnedSuggestions();
  } catch (e) {
    debug.error('Unpin failed:', e);
    return await getPinnedSuggestions();
  }
}

export function snoozeSuggestion(suggestionId, durationMs = 24 * 60 * 60 * 1000) {
  try {
    const snoozed = JSON.parse(localStorage.getItem(SNOOZED_SUGGESTIONS_KEY) || '{}');
    const typeId = suggestionId.split('_').slice(0, -1).join('_') || suggestionId;
    snoozed[typeId] = Date.now() + durationMs;
    localStorage.setItem(SNOOZED_SUGGESTIONS_KEY, JSON.stringify(snoozed));
    return true;
  } catch {
    return false;
  }
}

export function dismissSuggestion(suggestionId) {
  try {
    const dismissed = getDismissedIds();
    const typeId = suggestionId.split('_').slice(0, -1).join('_') || suggestionId;
    if (!dismissed.includes(typeId)) {
      dismissed.push(typeId);
      localStorage.setItem(DISMISSED_SUGGESTIONS_KEY, JSON.stringify(dismissed));
    }
    return true;
  } catch {
    return false;
  }
}

export function clearDismissed() {
  localStorage.removeItem(DISMISSED_SUGGESTIONS_KEY);
  localStorage.removeItem(SNOOZED_SUGGESTIONS_KEY);
  localStorage.removeItem(SUGGESTIONS_CACHE_KEY);
  localStorage.removeItem(SUGGESTIONS_TIMESTAMP_KEY);
  if (import.meta.env.DEV) console.log('%c[Insights] Cleared all', 'color: #22c55e; font-weight: bold');
}

export function invalidateSuggestionsCache() {
  localStorage.removeItem(SUGGESTIONS_CACHE_KEY);
  localStorage.removeItem(SUGGESTIONS_TIMESTAMP_KEY);
}

export function logInsightModalOpen(suggestion) {
  debug.log('Modal opened:', {
    type: suggestion.type?.id || suggestion.type,
    title: suggestion.title,
    reason: suggestion.reason,
    confidence: suggestion.confidence,
    timestamp: new Date().toISOString(),
  });
}

// Expose to window
if (typeof window !== 'undefined') {
  window.forceInsightsRefresh = invalidateSuggestionsCache;
  window.clearInsightsDismissed = clearDismissed;
  if (import.meta.env.DEV) console.log('%c[Insights V6] Commands: forceInsightsRefresh(), clearInsightsDismissed()', 'color: #8b5cf6');
}

export default {
  getSuggestionsForUser,
  getPinnedSuggestions,
  pinSuggestion,
  unpinSuggestion,
  snoozeSuggestion,
  dismissSuggestion,
  clearDismissed,
  invalidateSuggestionsCache,
  logInsightModalOpen,
  INSIGHT_TYPES,
};
